/*
 * Some declarations below are deliberately unused: they belong to UI that is commented out in place
 * (the application form and the job-details card meta). Deleting them would delete work in
 * progress, and whether that UI ships is a product decision rather than a lint one. Each one carries
 * its own `eslint-disable-next-line`, so the rule still catches anything new that goes unused here.
 */
/**
 * Job details page with Visual Theme - individual job posting view
 * Visual theme: Magazine-style layout with stunning photography and elegant typography
 *
 * URL: /[language]/trouver-un-emploi/offre/[publication-id]/[slug]
 * URL: /[language]/find-a-job/offer/[publication-id]/[slug]
 * URL: /[language]/encontrar-un-empleo/oferta/[publication-id]/[slug]
 *
 * @module /jobs/[...slug]
 */

import { ParsedUrlQuery } from 'querystring';
import { Publication, PublicationStatus } from '@bruce.work/bruce-doc-class-typescript/doc/Class/Publication';
import { Recorded } from '@bruce.work/bruce-doc-event-typescript/doc/Class';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useCallback, useRef, useState } from 'react';
import { BsCurrencyEuro } from 'react-icons/bs';
import { FiBriefcase, FiCalendar, FiHome, FiMapPin } from 'react-icons/fi';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import { PAGE_TYPE, PageBasicParams, PageBasicProps } from '../../interfaces';
import nextI18NextConfig from '../../next-i18next.config.js';

// Server-side only import (only used in getServerSideProps)
import { fetchFooterData, fetchNavbarData } from '../../utilities/cms';
import { cleanHtmlToText, formatTimeAgo, getJobImageUrl, parseUnderlineFromString, renderUnderline } from '../../utilities/functions';
import { defaultFooterData, defaultNavBarData } from './index.page';
import Button_v2 from '@/components/molecules/Button/index';
import Title_v2 from '@/components/molecules/Title/index';
import PageWrapper from '@/components/wrappers/PageWrapper';
import { startServerSide } from '@/parse/server';
import { FooterProps, NavbarProps } from '@/queries/components/blocks';
import devices from '@/utilities/devices';

// Default navbar and footer data (fallback when CMS fails)

/**
 * Generate URL-friendly slug from publication title
 */
const generateSlug = (title: string): string => {
	return title
		.toLowerCase()
		.trim()
		.replace(/[àáâãäå]/g, 'a')
		.replace(/[èéêë]/g, 'e')
		.replace(/[ìíîï]/g, 'i')
		.replace(/[òóôõö]/g, 'o')
		.replace(/[ùúûü]/g, 'u')
		.replace(/[ç]/g, 'c')
		.replace(/[ñ]/g, 'n')
		.replace(/[^a-z0-9\s-]/g, '') // Remove special characters
		.replace(/\s+/g, '-') // Replace spaces with dashes
		.replace(/-+/g, '-') // Remove multiple dashes
		.replace(/^-|-$/g, ''); // Remove leading/trailing dashes
};

/**
 * Generate locale-aware job listing URL
 */
export const generateJobListingUrl = (locale: string): string => {
	// Map locale to URL path segments
	const pathSegments: { [key: string]: string } = {
		fr: 'trouver-un-emploi/offres',
		en: 'find-a-job/offers',
		es: 'encontrar-un-empleo/ofertas',
	};

	const pathSegment = pathSegments[locale] || pathSegments.fr; // Default to French
	// Include language query parameter for proper locale handling
	return `/${locale ?? 'fr'}/${pathSegment}`;
};

/**
 * Page params for job details page
 * URL structure: /offre/[id]/[slug] where slug is any string checked an rerouted if publication title changes
 */
export interface PageParams extends PageBasicParams, ParsedUrlQuery {
	readonly publicationCode?: string;
	readonly slug?: string;
}

/**
 * Page props for job details page
 */
export interface PageProps extends PageBasicProps {
	readonly publication: Recorded<Publication>;
	readonly pageType: PAGE_TYPE;
	readonly title: string;
	readonly description: string;
	readonly navbarData?: NavbarProps;
	readonly footerData?: FooterProps;
}

const JobDetailsPage: React.FC<PageProps> = ({
	currentLang,
	currentPath,
	description,
	footerData,
	navbarData,
	pageType,
	publication,
	title,
}) => {
	const { t } = useTranslation(['common', 'jobs']);
	const router = useRouter();
	const [isApplicationOpen, setIsApplicationOpen] = useState(false);
	const applicationFormRef = useRef<HTMLDivElement>(null);
	const indeedData = publication.targetData && 'indeed' in publication.targetData
		? publication.targetData.indeed
		: null;

	const imageUrl = getJobImageUrl(publication, publication.mission?.job);
	const jobRemoteType = indeedData
		? (indeedData.remoteWork?.type === 'full')
			? t('jobs:card:remoteTypes:fullTime')
			: (indeedData.remoteWork?.type === 'hybrid')
				? t('jobs:card:remoteTypes:hybrid')
				: (indeedData.remoteWork?.type === 'none')
					? t('jobs:card:remoteTypes:none')
					: undefined
		: undefined;

	const addressLevel = indeedData?.addressLevel ?? 'state';

	const postalCode = (
		jobRemoteType
        &&
        indeedData
        &&
        indeedData.remoteWork?.type === 'full'
	)
		? ''
		: (['street', 'postalCode'].includes(addressLevel))
			? publication.address?.postalCode
			: null;

	const department = publication.address?.postalCode?.slice(0, 2) || null;

	const referenceDate = publication.publishedSince ?? publication.createdAt;

	// Get location from address or targetData
	const location = `${publication.address?.townName ?? ''} ${(postalCode || department) ? `(${postalCode ?? department})` : ''}`;

	// Job category belongs to the saved publication metadata.
	const category = publication.data?.facts?.category !== undefined
		? publication.data.facts.category : (publication.targetData && 'indeed' in publication.targetData)
			? publication.targetData.indeed?.jobType
			: undefined;

	// Get salary information
	const salaryInfo = publication.data?.facts?.salary !== undefined
		? publication.data.facts.salary : publication.targetData && 'indeed' in publication.targetData ?
			publication.targetData.indeed?.salaryData : null;

	const handleApplyClick = useCallback(() => {
		if (applicationFormRef.current) {
			applicationFormRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
		setIsApplicationOpen(true);
	}, []);

	const handleBackToJobs = useCallback(() => {
		router.push(generateJobListingUrl(currentLang));
	}, [router, currentLang]);

	const candidacyImporter = () => {
		(window as any).CANDIDACY_CONTEXT = {
			publicationId: publication.objectId,
			missionId: publication.mission?.objectId || null,
			missionData: publication.mission || null,
		};
		const script = document.createElement('script');
		script.src = '/guests/candidacy/bundles/production/bundle.js';
		script.async = true;
		document.body.appendChild(script);
	};

	/**
     * Generate structured data schema for job posting (schema.org JobPosting)
     */
	const jobSchemaConstructor = (jobPublication: Publication) => {
		// Employment type mapping based on category or targetData
		const employmentTypes: { [key: string]: string[] } = {
			CTT: ['FULL_TIME', 'PART_TIME', 'TEMPORARY'],
			CDD: ['FULL_TIME', 'TEMPORARY'],
			CDI: ['FULL_TIME'],
			STG: ['FULL_TIME', 'INTERN'],
		};

		// Employment type uses only the saved jobPublication metadata.
		const categoryFromTargetData = jobPublication.targetData && 'indeed' in jobPublication.targetData
			? jobPublication.targetData.indeed?.jobType
			: null;

		const finalCategory = jobPublication.data?.facts?.category !== undefined
			? jobPublication.data.facts.category : categoryFromTargetData;
		const employmentType = finalCategory && employmentTypes[finalCategory];

		// Date calculations
		const today = new Date();
		// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
		const closedDate = new Date(new Date().setDate(today.getDate() - 1));
		const publicationDate = referenceDate ?? today;
		const expiryDate = new Date(Math.max(
			publicationDate.getTime(),
			new Date().setDate(today.getDate() + 30), // Default 30 days expiry
		));

		// Get company information
		const companyName = jobPublication.targetData && 'indeed' in jobPublication.targetData
			? jobPublication.targetData.indeed?.company
			: jobPublication.organization?.name || 'Bruce';

		// Get salary information
		const salaryData = jobPublication.data?.facts?.salary !== undefined
			? jobPublication.data.facts.salary : jobPublication.targetData && 'indeed' in jobPublication.targetData
				? jobPublication.targetData.indeed?.salaryData
				: null;

		// Get remote work type
		const remoteWorkType = jobPublication.targetData && 'indeed' in jobPublication.targetData
			? jobPublication.targetData.indeed?.remoteWork?.type
			: null;

		// Construct job location
		const schemaAddressLevel = jobPublication.targetData && 'indeed' in jobPublication.targetData
			? jobPublication.targetData.indeed?.addressLevel ?? 'state'
			: 'state';

		const schemaPostalCode = (
			remoteWorkType === 'full' ||
            !['street', 'postalCode'].includes(schemaAddressLevel)
		) ? null : jobPublication.address?.postalCode;

		// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
		const schemaDepartment = jobPublication.address?.postalCode?.slice(0, 2) || null;

		// Clean HTML from title and description
		const cleanTitle = cleanHtmlToText(jobPublication.title || "Offre d'emploi");
		const cleanDescription = cleanHtmlToText(
			jobPublication.body || 'Description non disponible',
		);

		return [
			{
				'@context': 'https://schema.org/',
				'@type': 'JobPosting',
				'title': cleanTitle,
				'description': cleanDescription,
				'identifier': {
					'@type': 'PropertyValue',
					'name': 'Bruce',
					'value': jobPublication.objectId || 'unknown',
				},
				'datePosted': publicationDate.toISOString().slice(0, 16),
				'validThrough': expiryDate.toISOString().slice(0, 16),
				...(employmentType && { employmentType: JSON.stringify(employmentType) }),
				'hiringOrganization': {
					'@type': 'Organization',
					'name': companyName,
					'url': companyName === 'Bruce' ? 'https://www.bruce.work' : undefined,
					...(companyName === 'Bruce' && {
						contactPoint: [
							{
								'@type': 'ContactPoint',
								'telephone': '+33644604849',
								'contactType': 'Recruitment',
								'areaServed': 'FR',
							},
						],
						sameAs: [
							'https://facebook.com/TeamBruceFr',
							'https://instagram.com/team.bruce',
							'https://linkedin.com/company/bruce.work',
							'https://twitter.com/bruce_work',
							'https://www.bruce.work',
						],
						logo: 'https://www.bruce.work/guests/collections/picture/logo_bruce_square.png',
					}),
				},
				'jobLocation': {
					'@type': 'Place',
					'address': {
						'@type': 'PostalAddress',
						'addressLocality': jobPublication.address?.townName || 'France',
						...(schemaPostalCode && { postalCode: schemaPostalCode }),
						'addressCountry': 'FR',
					},
				},
				...(salaryData?.formattedWage && {
					baseSalary: {
						'@type': 'MonetaryAmount',
						'currency': 'EUR',
						'value': {
							'@type': 'QuantitativeValue',
							'value': salaryData.formattedWage,
							'unitText': 'YEAR',
						},
					},
				}),
				...(remoteWorkType && {
					workLocation: remoteWorkType === 'full' ?
						{
							'@type': 'Place',
							'name': 'Remote',
						} : undefined,
				}),
			},
			{
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				'itemListElement': [
					{
						'@type': 'ListItem',
						'position': 1,
						'name': 'Accueil',
						'item': 'https://www.bruce.work/',
					},
					{
						'@type': 'ListItem',
						'position': 2,
						'name': 'Trouver un emploi',
						'item': 'https://www.bruce.work/trouver-un-emploi/',
					},
					{
						'@type': 'ListItem',
						'position': 3,
						'name': 'Offres',
						'item': 'https://www.bruce.work/trouver-un-emploi/offres',
					},
					{
						'@type': 'ListItem',
						'position': 4,
						'name': jobPublication.title || 'Offre',
						'item': `https://www.bruce.work${currentPath}`,
					},
				],
			},
		];
	};

	return (
		<PageWrapper
			currentLang={currentLang}
			currentPath={currentPath}
			description={description}
			footerData={footerData}
			isv2={true}
			navbarData={navbarData}
			pageType={pageType}
			title={title}
		>
			<Head>
				<script
					key={`jobJSON-${publication.objectId}`}
					type='application/ld+json'
					dangerouslySetInnerHTML={{ __html: JSON.stringify(jobSchemaConstructor(publication)) }}
				/>
			</Head>
			{/* Hero Section */}
			<VisualHeroSection background={imageUrl ?? '/images/feature/Mv1whyyQ0d_3_2_480_3x.jpg'}>
				<VisualHeroOverlay />
				<VisualHeroContainer>
					<VisualBackButton onClick={handleBackToJobs}>
						← {t('jobs:details:backToJobs')}
					</VisualBackButton>
				</VisualHeroContainer>
				<VisualHeroContent>
					<VisualHeroMain>
						<Title_v2
							align={'center'}
							white
							padding='0 0 0 0'
							underlineColor={'red'}
						>
							{renderUnderline([{ text: publication.title ?? '' }])}
						</Title_v2>
						<VisualJobMeta>
							{location && (
								<VisualJobMetaItem>
									<FiMapPin />
									<span>{location}</span>
								</VisualJobMetaItem>
							)}
							<VisualJobMetaItem>
								<FiCalendar />
								<span>{referenceDate ? formatTimeAgo(referenceDate, t) : t('jobs:details:recentlyPosted')}</span>
							</VisualJobMetaItem>
						</VisualJobMeta>
					</VisualHeroMain>
					<VisualHeroActions>
						<Button_v2
							href=''
							onClick={handleApplyClick}
							text={t('jobs:details:applyNow')}
							version='whiteOnRed'
							fontSize='0.875rem'
						/>
					</VisualHeroActions>
				</VisualHeroContent>
			</VisualHeroSection>

			{/* Main Content */}
			<VisualContainer>
				<VisualContent>
					<VisualMainColumn>
						{/* Job Description */}
						<VisualSection>
							<Title_v2
								fontSize='1.8rem'
								underlineColor="red"
								padding='0 0 12px 0'
							>
								{renderUnderline(parseUnderlineFromString(t('jobs:details:jobDescription')))}
							</Title_v2>
							<VisualJobDescription>
								{publication.body ? (
									<div dangerouslySetInnerHTML={{ __html: publication.body.replace(/\n/g, '<br>') }} />
								) : (
									<p>{t('jobs:details:descriptionNotAvailable')}</p>
								)}
							</VisualJobDescription>
						</VisualSection>
					</VisualMainColumn>
					<VisualSidebarColumn>
						{/* Job Details Card */}
						<VisualDetailsCard>
							{location && (
								<VisualDetailItem>
									<VisualDetailLabel>
										<FiMapPin />
										{t('jobs:details:location')}
									</VisualDetailLabel>
									<VisualDetailValue>{location}</VisualDetailValue>
								</VisualDetailItem>
							)}

							{category && (
								<VisualDetailItem>
									<VisualDetailLabel>
										<FiBriefcase />
										{t('jobs:details:contractType')}
									</VisualDetailLabel>
									<VisualDetailValue>{category}</VisualDetailValue>
								</VisualDetailItem>
							)}

							{salaryInfo?.formattedWage && (
								<VisualDetailItem>
									<VisualDetailLabel>
										<BsCurrencyEuro />
										{t('jobs:details:salary')}
									</VisualDetailLabel>
									<VisualDetailValue>{salaryInfo.formattedWage}</VisualDetailValue>
								</VisualDetailItem>
							)}

							{jobRemoteType && (
								<VisualDetailItem>
									<VisualDetailLabel>
										<FiHome />
										{t('jobs:details:remoteType')}
									</VisualDetailLabel>
									<VisualDetailValue>{jobRemoteType}</VisualDetailValue>
								</VisualDetailItem>
							)}

							<VisualDetailItem>
								<VisualDetailLabel>
									<FiCalendar />
									{t('jobs:details:postedOnTitle')}
								</VisualDetailLabel>
								<VisualDetailValue>
									{referenceDate ? formatTimeAgo(referenceDate, t) : t('jobs:details:recentlyPosted')}
								</VisualDetailValue>
							</VisualDetailItem>
						</VisualDetailsCard>

						{/* Apply CTA Card */}
						<VisualCtaCard>
							<VisualCtaTitle>
								{t('jobs:details:interestedInThisPosition')}
							</VisualCtaTitle>
							<VisualCtaDescription>
								{t('jobs:details:applyDescription')}
							</VisualCtaDescription>
							<Button_v2
								href=''
								onClick={handleApplyClick}
								text={t('jobs:details:applyNow')}
								version='whiteOnTransparent'
								fontSize='0.875rem'
							/>
						</VisualCtaCard>
					</VisualSidebarColumn>
				</VisualContent>
			</VisualContainer>

			{/* Application Form Section */}
			<InView triggerOnce onChange={(inView) => inView && candidacyImporter()} rootMargin="250px">
				<VisualApplicationSection ref={applicationFormRef} isOpen={isApplicationOpen}>
					<VisualContainer>
						<Title_v2
							underlineColor="red"
							align="center"
							padding="0 0 3rem 0"
						>
							{renderUnderline(parseUnderlineFromString(t('jobs:application:title')))}
						</Title_v2>
						<div id="candidacy" />
						{/* <ApplicationForm publication={publication} /> */}
					</VisualContainer>
				</VisualApplicationSection>
			</InView>
		</PageWrapper>
	);
};

// Application Form Component
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: used only by the commented-out UI below.
const ApplicationForm: React.FC<{ publication: Recorded<Publication> }> = ({ publication }) => {
	const { t } = useTranslation(['common', 'jobs']);
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		coverLetter: '',
		cvFile: null as File | null,
		gdprConsent: false,
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);

	const handleInputChange = useCallback((field: string, value: any) => {
		setFormData(prev => ({
			...prev,
			[field]: value,
		}));
	}, []);

	const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			// Check file type and size
			const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
			if (!allowedTypes.includes(file.type)) {
				alert(t('jobs:application:fileTypeError'));
				return;
			}
			if (file.size > 5 * 1024 * 1024) { // 5MB limit
				alert(t('jobs:application:fileSizeError'));
				return;
			}
			setFormData(prev => ({ ...prev, cvFile: file }));
		}
	}, [t]);

	const handleSubmit = useCallback(async (event: React.FormEvent) => {
		event.preventDefault();

		if (!formData.firstName || !formData.lastName || !formData.email || !formData.cvFile || !formData.gdprConsent) {
			alert(t('jobs:application:requiredFieldsError'));
			return;
		}

		setIsSubmitting(true);

		try {
			// Here you would implement the actual form submission logic
			// For now, we'll simulate a submission
			await new Promise(resolve => setTimeout(resolve, 2000));
			setIsSubmitted(true);
		} catch (error) {
			console.error('Application submission error:', error);
			alert(t('jobs:application:submissionError'));
		} finally {
			setIsSubmitting(false);
		}
	}, [formData, t]);

	if (isSubmitted) {
		return (
			<VisualSuccessMessage>
				<span>✅</span>
				<h3>{t('jobs:application:successTitle')}</h3>
				<p>{t('jobs:application:successMessage')}</p>
			</VisualSuccessMessage>
		);
	}

	return (
		<VisualApplicationForm onSubmit={handleSubmit}>
			<VisualFormGrid>
				<VisualInputGroup>
					<VisualLabel>{t('jobs:application:firstName')} *</VisualLabel>
					<VisualInput
						type="text"
						value={formData.firstName}
						onChange={(e) => handleInputChange('firstName', e.target.value)}
						placeholder={t('jobs:application:firstNamePlaceholder')}
						required
					/>
				</VisualInputGroup>

				<VisualInputGroup>
					<VisualLabel>{t('jobs:application:lastName')} *</VisualLabel>
					<VisualInput
						type="text"
						value={formData.lastName}
						onChange={(e) => handleInputChange('lastName', e.target.value)}
						placeholder={t('jobs:application:lastNamePlaceholder')}
						required
					/>
				</VisualInputGroup>

				<VisualInputGroup>
					<VisualLabel>{t('jobs:application:email')} *</VisualLabel>
					<VisualInput
						type="email"
						value={formData.email}
						onChange={(e) => handleInputChange('email', e.target.value)}
						placeholder={t('jobs:application:emailPlaceholder')}
						required
					/>
				</VisualInputGroup>

				<VisualInputGroup>
					<VisualLabel>{t('jobs:application:phone')}</VisualLabel>
					<VisualInput
						type="tel"
						value={formData.phone}
						onChange={(e) => handleInputChange('phone', e.target.value)}
						placeholder={t('jobs:application:phonePlaceholder')}
					/>
				</VisualInputGroup>

				<VisualInputGroup fullWidth>
					<VisualLabel>{t('jobs:application:cvUpload')} *</VisualLabel>
					<VisualFileInput>
						<VisualFileInputButton htmlFor="cv-upload">
							📎 {formData.cvFile ? formData.cvFile.name : t('jobs:application:selectFile')}
						</VisualFileInputButton>
						<VisualHiddenInput
							id="cv-upload"
							type="file"
							accept=".pdf,.doc,.docx"
							onChange={handleFileUpload}
						/>
					</VisualFileInput>
					<VisualHelpText>
						{t('jobs:application:fileHelp')}
					</VisualHelpText>
				</VisualInputGroup>

				<VisualInputGroup fullWidth>
					<VisualLabel>{t('jobs:application:coverLetter')}</VisualLabel>
					<VisualTextarea
						value={formData.coverLetter}
						onChange={(e) => handleInputChange('coverLetter', e.target.value)}
						placeholder={t('jobs:application:coverLetterPlaceholder')}
						rows={5}
					/>
				</VisualInputGroup>

				<VisualInputGroup fullWidth>
					<VisualCheckboxContainer>
						<VisualCheckbox
							type="checkbox"
							id="gdpr-consent"
							checked={formData.gdprConsent}
							onChange={(e) => handleInputChange('gdprConsent', e.target.checked)}
							required
						/>
						<VisualCheckboxLabel htmlFor="gdpr-consent">
							{t('jobs:application:gdprConsent')} *
						</VisualCheckboxLabel>
					</VisualCheckboxContainer>
				</VisualInputGroup>
			</VisualFormGrid>

			<VisualSubmitContainer>
				<Button_v2
					href=''
					onClick={() => { }}
					text={isSubmitting ? t('jobs:application:submitting') : t('jobs:application:submitApplication')}
					version='whiteOnRed'
					fontSize='0.875rem'
				/>
			</VisualSubmitContainer>
		</VisualApplicationForm>
	);
};

// Styled Components - Visual Theme

const VisualHeroSection = styled.section<{ background: string }>`
    position: relative;
    height: 60vh;
    min-height: 500px;
    background: linear-gradient(rgba(31, 41, 55, 0.3), rgba(31, 41, 55, 0.7)), url(${props => props.background});
    background-size: cover;
    background-position: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    overflow: hidden;
    
    @media ${devices.mobile} {
        height: 50vh;
        min-height: 400px;
    }
`;

const VisualHeroOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(217, 119, 6, 0.2) 100%);
`;

const VisualHeroContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 2rem;
    
    @media ${devices.mobile} {
        padding: 1rem;
    }
`;

const VisualHeroContent = styled.div`
    position: relative;
    z-index: 2;
    text-align: center;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    min-height: 500px;
    
    @media ${devices.mobile} {
        min-height: 400px;
    }
`;

const VisualHeroMain = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
    gap: 2rem;
    
    @media ${devices.mobile} {
        gap: 1.5rem;
    }
`;

const VisualBackButton = styled.button`
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    padding: 12px 24px;
    border-radius: 8px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: rgba(255, 255, 255, 0.5);
        transform: translateY(-2px);
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualJobTitle = styled.h1`
    font-size: ${({ theme }) => theme.fontSize.XHEADLINE};
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 1.5rem;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
    
    @media ${devices.mobile} {
        font-size: ${({ theme }) => theme.fontSize.XXLARGE};
        margin-bottom: 1rem;
    }
`;

const VisualJobMeta = styled.div`
    display: flex;
    justify-content: center;
    gap: 2rem;
    flex-wrap: wrap;
    
    @media ${devices.mobile} {
        gap: 1rem;
    }
`;

const VisualJobMetaItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.2);
    padding: 10px 16px;
    border-radius: 20px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 500;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    span:first-child {
        font-size: 16px;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualJobTags = styled.div`
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 3rem;
    flex-wrap: wrap;
    
    @media ${devices.mobile} {
        margin-bottom: 2rem;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualJobTag = styled.span<{ variant?: 'primary' | 'secondary' }>`
    padding: 8px 16px;
    border-radius: 20px;
    font-size: ${({ theme }) => theme.fontSize.XSMALL};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: ${({ variant, theme }) => {
		switch (variant) {
			case 'primary': return theme.color.red;
			case 'secondary': return theme.color.beige;
			default: return 'rgba(255, 255, 255, 0.3)';
		}
	}};
    color: ${({ variant }) => variant === 'secondary' ? '#000' : '#fff'};
    border: 1px solid ${({ variant }) => variant ? 'transparent' : 'rgba(255, 255, 255, 0.3)'};
    backdrop-filter: ${({ variant }) => variant ? 'none' : 'blur(10px)'};
`;

const VisualHeroActions = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-end;
    padding-bottom: 3rem;
    
    @media ${devices.mobile} {
        padding-bottom: 2rem;
    }
`;

const VisualContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
`;

const VisualContent = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 4rem;
    padding: 4rem 0;
    
    @media (${devices.minTablet}) and (${devices.maxTablet}) {
        grid-template-columns: 1fr;
        gap: 2rem;
        padding: 2rem 0;
    }
`;

const VisualMainColumn = styled.div``;

const VisualSidebarColumn = styled.div`
    @media (${devices.minTablet}) and (${devices.maxTablet}) {
        order: -1;
    }
`;

const VisualSection = styled.section`
    margin-bottom: 3rem;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualSectionTitle = styled.h2`
    font-size: ${({ theme }) => theme.fontSize.XLARGE};
    font-weight: 700;
    color: ${({ theme }) => theme.color.black};
    margin-bottom: 1.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 3px solid ${({ theme }) => theme.color.red};
    display: inline-block;
`;

const VisualJobDescription = styled.div`
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    line-height: 1.8;
    color: ${({ theme }) => theme.color.blackLight};
    
    p {
        margin-bottom: 1.5rem;
        
        &:last-child {
            margin-bottom: 0;
        }
    }
    
    ul, ol {
        margin: 1.5rem 0;
        padding-left: 2rem;
        
        li {
            margin-bottom: 0.5rem;
        }
    }
    
    strong {
        color: ${({ theme }) => theme.color.black};
        font-weight: 800;
    }

    b {
        color: ${({ theme }) => theme.color.black};
        font-weight: 800;
    }
`;

const VisualDetailsCard = styled.div`
    background: ${({ theme }) => theme.color.white};
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    border: 1px solid ${({ theme }) => theme.color.greyLight};
    margin-bottom: 2rem;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
const VisualCardTitle = styled.h3`
    font-size: ${({ theme }) => theme.fontSize.LARGE};
    font-weight: 700;
    color: ${({ theme }) => theme.color.black};
    margin-bottom: 1.5rem;
    text-align: center;
`;

const VisualDetailItem = styled.div`
    margin-bottom: 1.5rem;
    
    &:last-child {
        margin-bottom: 0;
    }
`;

const VisualDetailLabel = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 600;
    color: ${({ theme }) => theme.color.blackLight};
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    span {
        font-size: 16px;
    }
`;

const VisualDetailValue = styled.div`
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ theme }) => theme.color.black};
    font-weight: 500;
`;

const VisualCtaCard = styled.div`
    background: linear-gradient(135deg, ${({ theme }) => theme.color.prune} 0%, ${({ theme }) => theme.color.pruneLight} 100%);
    border-radius: 12px;
    padding: 2rem;
    color: white;
    text-align: center;
    box-shadow: 0 8px 32px rgba(${({ theme }) => theme.color.rgbPrune}, 0.3);
`;

const VisualCtaTitle = styled.h3`
    font-size: ${({ theme }) => theme.fontSize.LARGE};
    font-weight: 700;
    margin-bottom: 1rem;
`;

const VisualCtaDescription = styled.p`
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    line-height: 1.6;
    margin-bottom: 2rem;
    opacity: 0.9;
`;

const VisualApplicationSection = styled.section<{ isOpen: boolean }>`
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    padding: 4rem 0;
    border-top: 4px solid ${({ theme }) => theme.color.beige};
    opacity: ${({ isOpen }) => isOpen ? 1 : 0.7};
    transition: opacity 0.5s ease;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media ${devices.mobile} {
        padding: 2rem 0;
    }
`;

const VisualApplicationForm = styled.form`
    background: ${({ theme }) => theme.color.white};
    border-radius: 16px;
    padding: 3rem;
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
    max-width: 800px;
    margin: 0 auto;
    
    @media ${devices.mobile} {
        padding: 2rem 1rem;
    }
`;

const VisualFormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
    
    @media ${devices.mobile} {
        grid-template-columns: 1fr;
        gap: 1.5rem;
    }
`;

const VisualInputGroup = styled.div<{ fullWidth?: boolean }>`
    grid-column: ${({ fullWidth }) => fullWidth ? '1 / -1' : 'auto'};
`;

const VisualLabel = styled.label`
    display: block;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    font-weight: 600;
    color: ${({ theme }) => theme.color.black};
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const VisualInput = styled.input`
    width: 100%;
    padding: 1rem;
    border: 2px solid ${({ theme }) => theme.color.greyLight};
    border-radius: 8px;
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    background: ${({ theme }) => theme.color.white};
    transition: all 0.3s ease;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.color.beige};
        box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.color.greyDarker};
    }
`;

const VisualTextarea = styled.textarea`
    width: 100%;
    padding: 1rem;
    border: 2px solid ${({ theme }) => theme.color.greyLight};
    border-radius: 8px;
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    background: ${({ theme }) => theme.color.white};
    transition: all 0.3s ease;
    resize: vertical;
    font-family: inherit;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.color.beige};
        box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.1);
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.color.greyDarker};
    }
`;

const VisualFileInput = styled.div`
    position: relative;
`;

const VisualFileInputButton = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 1rem;
    border: 2px dashed ${({ theme }) => theme.color.greyLight};
    border-radius: 8px;
    background: ${({ theme }) => theme.color.greyLight};
    font-size: ${({ theme }) => theme.fontSize.DEFAULT};
    color: ${({ theme }) => theme.color.blackLight};
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: center;
    
    &:hover {
        border-color: ${({ theme }) => theme.color.beige};
        background: rgba(217, 119, 6, 0.05);
    }
`;

const VisualHiddenInput = styled.input`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
`;

const VisualHelpText = styled.small`
    display: block;
    margin-top: 0.5rem;
    font-size: ${({ theme }) => theme.fontSize.XSMALL};
    color: ${({ theme }) => theme.color.greyDarker};
`;

const VisualCheckboxContainer = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const VisualCheckbox = styled.input`
    width: 18px;
    height: 18px;
    margin: 2px 0;
    cursor: pointer;
`;

const VisualCheckboxLabel = styled.label`
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ theme }) => theme.color.blackLight};
    line-height: 1.6;
    cursor: pointer;
    text-transform: none;
    letter-spacing: normal;
`;

const VisualSubmitContainer = styled.div`
    text-align: center;
    border-top: 1px solid ${({ theme }) => theme.color.greyLight};
    padding-top: 2rem;
`;

const VisualSuccessMessage = styled.div`
    text-align: center;
    padding: 3rem 2rem;
    
    span {
        font-size: 64px;
        display: block;
        margin-bottom: 1rem;
    }
    
    h3 {
        font-size: ${({ theme }) => theme.fontSize.XLARGE};
        font-weight: 700;
        color: ${({ theme }) => theme.color.black};
        margin-bottom: 1rem;
    }
    
    p {
        font-size: ${({ theme }) => theme.fontSize.DEFAULT};
        color: ${({ theme }) => theme.color.blackLight};
        line-height: 1.6;
        max-width: 500px;
        margin: 0 auto;
    }
`;

export const getServerSideProps: GetServerSideProps<PageProps, PageParams> = async (context) => {
	const { locale = 'fr', query } = context;
	const { publicationCode, publicationSlug, language: queryLanguage } = query as PageParams & { publicationSlug?: string, language?: string };

	// Use language from query parameter if provided, otherwise fall back to locale
	const currentLang = queryLanguage || locale;

	if (!(publicationCode)) {
		return {
			notFound: true,
		};
	}

	try {
		const publications = await startServerSide.query<Publication>({
			data: {
				class: {
					fields: [
						'body',
						'createdAt',
						'code',
						'mission.job.image',
						'organization.name',
						'targetData',
						'data',
						'title',
						'address',
						'objectId',
					],
					name: 'Publication',
				},
				where: {
					field: {
						code: { '=': publicationCode },
						status: {
							'∈': [PublicationStatus.ACTIVE, PublicationStatus.PENDING, PublicationStatus.UNSYNCHRONIZED],
						},
					},
					limit: 1,
					order: [],
					start: 0,
				},
			},
		},
		);

		if (!(publications) || publications.length === 0) {
			return {
				notFound: true,
			};
		}
		const publication = publications[0];
		const correctSlug = publication.title ? generateSlug(publication.title) : 'offre';

		// SEO redirect: ensure URL has the correct slug
		if (!(publicationSlug) || publicationSlug !== correctSlug) {
			// Map locale to URL path segments
			const pathSegments: { [key: string]: string } = {
				fr: 'trouver-un-emploi/offre',
				en: 'find-a-job/offer',
				es: 'encontrar-un-empleo/oferta',
			};
			const filteredQuery = Object.fromEntries(Object.entries(query).filter(([key]) => !['publicationCode', 'publicationSlug', 'language', 'slug'].includes(key)));
			const pathSegment = pathSegments[currentLang] ?? pathSegments.fr;
			const correctPath = `/${currentLang ?? 'fr'}/${pathSegment ?? 'fr'}/${publicationCode}/${correctSlug}${Object.keys(filteredQuery).length > 0 ? `?${new URLSearchParams(filteredQuery as Record<string, string>).toString()}` : ''}`;

			return {
				redirect: {
					destination: correctPath,
					permanent: false,
				},
			};
		}

		// Generate meta title and description
		// Clean HTML tags from description for SEO meta description
		// eslint-disable-next-line @typescript-eslint/no-unused-vars -- dormant: leftover of that commented-out UI.
		const companyName = publication.targetData && 'indeed' in publication.targetData ?
			publication.targetData.indeed?.company :
			publication.organization?.name;

		const title = `${cleanHtmlToText(publication.title ?? "Offre d'emploi")} | Bruce`;
		const description = cleanHtmlToText(publication.body || '', 160);

		// Fetch localized navbar and footer data from CMS
		const [navbarData, footerData] = await Promise.all([
			fetchNavbarData(currentLang),
			fetchFooterData(currentLang),
		]);

		return {
			props: {
				...(await serverSideTranslations(currentLang, ['common', 'jobs'], nextI18NextConfig)),
				publication,
				pageType: PAGE_TYPE.b2c,
				currentPath: context.req.url || '',
				currentLang,
				title,
				description,
				navbarData: navbarData || defaultNavBarData,
				footerData: footerData || defaultFooterData,
			},
		};
	} catch (error) {
		console.error('Error in getServerSideProps for job details:', error);

		return {
			notFound: true,
		};
	}
};

export default JobDetailsPage;
